import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FieldError, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import Stack from "@mui/material/Stack"
import { registerUserSchema } from "validations/auth"
import { useAuth } from "context/AuthContext"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { MINIMUM_PASSWORD_LENGTH, MINIMUM_USERNAME_LENGTH } from "constants/index"
import {
  INVALID_STRING_ZOD_ERROR,
  MIN_LENGTH_ZOD_ERROR,
  SERVER_ERROR,
} from "constants/errors"
import VisibleIcon from "components/atoms/_icons/VisibleIcon"
import InvisibleIcon from "components/atoms/_icons/InvisibleIcon"
import TextField from "components/atoms/TextField"
import CTA from "components/atoms/CTA"
import LoginRegisterQuestionLink from "components/molecules/LoginRegisterQuestionLink"

export default function RegisterForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { register: signup, isAuthenticated, isUserRegisterLoading } = useAuth()

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields }
  } = useForm<registerRequestPayload>({
    resolver: zodResolver(registerUserSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: {
      email: "",
      username: "",
      password: "",
      confirmPassword: ""
    }
  })

  useEffect(() => {
    if (!isAuthenticated) return
    navigate("/feed")
  }, [isAuthenticated])

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
    handleErrors()
  }, [errors])

  function handleErrors() {
    if (errors.username) {
      setGlobalErrorForInvalidUsername(errors.username)
    } else if (errors.password) {
      setGlobalErrorForInvalidPassword(errors.password)
    } else if (errors.confirmPassword && errors.confirmPassword.type === "custom") {
      setGlobalErrorForInvalidConfirmPassword(errors.confirmPassword)
    }
  }

  function setGlobalErrorForInvalidUsername(usernameError: FieldError) {
    if (usernameError.type === "custom") {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `username_${INVALID_STRING_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.invalidCharacterInUsernameError")
      }))
    } else if (usernameError.type === MIN_LENGTH_ZOD_ERROR) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `username_${MIN_LENGTH_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.minLengthError", { field: t("auth.username"), minLength: MINIMUM_USERNAME_LENGTH })
      }))
    }
  }

  function setGlobalErrorForInvalidPassword(passwordError: FieldError) {
    if (passwordError.type === MIN_LENGTH_ZOD_ERROR) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `password_${MIN_LENGTH_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.minLengthError", { field: t("auth.password"), minLength: MINIMUM_PASSWORD_LENGTH })
      }))
    }
  }

  function setGlobalErrorForInvalidConfirmPassword(confirmPasswordError: FieldError) {
    if (confirmPasswordError.type === "custom") {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: "confirmPassword",
        show: true,
        status: 400,
        message: "errorMessage.passwordsDontMatchException"
      }))
    }
  }

  async function registerUser(value: registerRequestPayload) {
    try {
      await signup(value)
    } catch (err) {
      console.error("Error happened in registerUser method. Error:", err)
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: SERVER_ERROR,
        show: true,
        status: 500,
        message: `general.${SERVER_ERROR}`
      }))
    }
  }

  function togglePasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowPassword(!showPassword)
  }

  function toggleConfirmPasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowConfirmPassword(!showConfirmPassword)
  }

  return (
    <form onSubmit={handleSubmit(registerUser)} style={{ width: "100%" }}>
      <Stack width="100%" marginTop={4} flexGrow={1}>
        <TextField
          id="username"
          label={t("auth.username")}
          required={true}
          register={register}
          setValue={setValue}
          error={errors.username}
          direction={"rtl"}
        />
        <TextField
          id="email"
          label={t("user.email")}
          required={true}
          type="email"
          register={register}
          setValue={setValue}
          error={errors.email}
          direction={"rtl"}
        />
        <TextField
          id="password"
          label={t("auth.password")}
          type={showPassword ? "text" : "password"}
          required={true}
          startAdornmentIcon={showPassword ? <VisibleIcon/> : <InvisibleIcon/>}
          onIconClick={togglePasswordVisibility}
          register={register}
          setValue={setValue}
          error={errors.username ? undefined : errors.password}
          direction={"rtl"}
        />
        <TextField
          id="confirmPassword"
          label={t("auth.confirmPassword")}
          type={showConfirmPassword ? "text" : "password"}
          required={true}
          startAdornmentIcon={showConfirmPassword ? <VisibleIcon/> : <InvisibleIcon/>}
          onIconClick={toggleConfirmPasswordVisibility}
          register={register}
          setValue={setValue}
          error={(errors.username || errors.password) ? undefined : errors.confirmPassword}
          direction={"rtl"}
        />
      </Stack>
      <CTA
        variant="contained"
        type="submit"
        disabled={Object.keys(dirtyFields).length !== 4 || isUserRegisterLoading}
        loading={isUserRegisterLoading}
        onClick={handleSubmit(registerUser)}
      >
        {t("auth.register")}
      </CTA>
      <LoginRegisterQuestionLink
        mainQuestion={t("auth.haveAnAccountQuestion")}
        link={{
          label: t("auth.login"),
          href: "/login"
        }}
      />
    </form>
  )
}
